import { useState, useEffect, useCallback } from 'react'
import { AdminAlert, AdminComingSoon, isNotBuiltYet } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import {
  adminGetTasks,
  adminCreateTask,
  adminUpdateTask,
  adminDeactivateTask,
  adminDeleteTask,
  adminGetTaskStats,
  adminManualCompleteTask,
  adminGetTaskSubmissions,
  adminApproveTaskSubmission,
  adminRejectTaskSubmission,
} from '../../../services/admin'

const TASK_TYPES = [
  { value: 'like', label: 'Like' },
  { value: 'comment', label: 'Comment' },
  { value: 'retweet', label: 'Repost' },
  { value: 'follow', label: 'Follow' },
  { value: 'post_keyword', label: 'Post w/ Keyword' },
  { value: 'custom', label: 'Custom' },
]

const STATUS_FILTERS = [
  { key: undefined, label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'paused', label: 'Paused' },
  { key: 'ended', label: 'Ended' },
]

const SUBMISSION_FILTERS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: undefined, label: 'All' },
]

function fmt(n) {
  const num = Number(n)
  return Number.isFinite(num) ? num.toLocaleString() : n ?? '—'
}

// Shared confirm/side dialog shell, same pattern as OfferwallManagement.
function Dialog({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className={`bg-surface border-4 border-on-background w-full ${wide ? 'max-w-lg' : 'max-w-sm'} flex flex-col gap-4 p-6 max-h-[90vh] overflow-y-auto`}
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] uppercase tracking-widest">{title}</h2>
          <button onClick={onClose}>
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">{label}</label>
      {children}
    </div>
  )
}

const inputCls = "bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"

// ── Create / Edit task form ──────────────────────────────────────────────
function TaskFormDialog({ task, onClose, onSaved }) {
  const isEdit = !!task
  const [form, setForm] = useState({
    title: task?.title ?? '',
    description: task?.description ?? '',
    type: task?.type ?? 'custom',
    targetUrl: task?.targetUrl ?? '',
    requiredKeywords: (task?.requiredKeywords ?? []).join(', '),
    rewardAmount: task?.rewardAmount ?? '',
    completionCap: task?.completionCap ?? '',
    recurring: task?.recurring ?? false,
    cooldownHours: task?.cooldownHours ?? '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const set = (key) => (e) => {
    const val = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value
    setForm(f => ({ ...f, [key]: val }))
  }

  const save = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.rewardAmount) {
      setError('Title, description and reward amount are required')
      return
    }
    if (form.recurring && !form.cooldownHours) {
      setError('Cooldown hours is required for recurring tasks')
      return
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      type: form.type,
      targetUrl: form.targetUrl.trim() || undefined,
      requiredKeywords: form.type === 'post_keyword'
        ? form.requiredKeywords.split(',').map(k => k.trim()).filter(Boolean)
        : undefined,
      rewardAmount: Number(form.rewardAmount),
      completionCap: form.completionCap ? Number(form.completionCap) : undefined,
      recurring: !!form.recurring,
      cooldownHours: form.recurring ? Number(form.cooldownHours) : undefined,
    }

    setLoading(true)
    setError(null)
    try {
      if (isEdit) await adminUpdateTask(task.id, payload)
      else await adminCreateTask(payload)
      onSaved()
    } catch (err) {
      setError(err.message ?? 'Failed to save task')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title={isEdit ? 'Edit Task' : 'Create Task'} onClose={onClose} wide>
      {error && <AdminAlert type="error" message={error} />}

      <Field label="Title">
        <input className={inputCls} value={form.title} onChange={set('title')} />
      </Field>

      <Field label="Instructions">
        <textarea rows={3} className={`${inputCls} resize-y`} value={form.description} onChange={set('description')} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Type">
          <select className={inputCls} value={form.type} onChange={set('type')}>
            {TASK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Reward (CPT)">
          <input type="number" min="1" className={inputCls} value={form.rewardAmount} onChange={set('rewardAmount')} />
        </Field>
      </div>

      <Field label="Target link (optional)">
        <input className={inputCls} placeholder="https://x.com/chicagonews/status/..." value={form.targetUrl} onChange={set('targetUrl')} />
      </Field>

      {form.type === 'post_keyword' && (
        <Field label="Required keywords (comma separated)">
          <input className={inputCls} placeholder="$CPT, #ChicagoSeason1" value={form.requiredKeywords} onChange={set('requiredKeywords')} />
        </Field>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Completion cap (optional)">
          <input type="number" min="1" className={inputCls} placeholder="Unlimited" value={form.completionCap} onChange={set('completionCap')} />
        </Field>
        <Field label="Recurring">
          <label className="flex items-center gap-2 h-full px-1">
            <input type="checkbox" checked={form.recurring} onChange={set('recurring')} className="w-4 h-4" />
            <span className="text-[12px] font-bold">Repeatable task</span>
          </label>
        </Field>
      </div>

      {form.recurring && (
        <Field label="Cooldown between submissions (hours)">
          <input type="number" min="1" className={inputCls} value={form.cooldownHours} onChange={set('cooldownHours')} />
        </Field>
      )}

      <div className="flex gap-2 mt-1">
        <button onClick={onClose} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={save} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-50">
          {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Task'}
        </button>
      </div>
    </Dialog>
  )
}

// ── Stats dialog ──────────────────────────────────────────────────────────
function StatsDialog({ taskId, onClose }) {
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    adminGetTaskStats(taskId).then(setStats).catch(setError)
  }, [taskId])

  return (
    <Dialog title="Task Stats" onClose={onClose}>
      {error && <AdminAlert type="error" message={error.message ?? 'Failed to load stats'} />}
      {!stats && !error ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6" />
          <Skeleton className="h-6" />
          <Skeleton className="h-6" />
        </div>
      ) : stats && (
        <div className="flex flex-col gap-2 text-[12px]">
          <div className="flex justify-between"><span className="text-on-surface-variant">Pending</span><span className="font-bold">{fmt(stats.submissions?.pending)}</span></div>
          <div className="flex justify-between"><span className="text-on-surface-variant">Approved</span><span className="font-bold">{fmt(stats.submissions?.approved)}</span></div>
          <div className="flex justify-between"><span className="text-on-surface-variant">Rejected</span><span className="font-bold">{fmt(stats.submissions?.rejected)}</span></div>
          <div className="flex justify-between border-t-2 border-on-background/10 pt-2"><span className="text-on-surface-variant">Completions</span><span className="font-bold">{fmt(stats.completionsCount)}{stats.completionCap ? ` / ${stats.completionCap}` : ''}</span></div>
          <div className="flex justify-between"><span className="text-on-surface-variant">Total CPT paid</span><span className="font-bold text-primary-container">{fmt(stats.totalCptPaid)}</span></div>
        </div>
      )}
    </Dialog>
  )
}

// ── Manual complete dialog ──────────────────────────────────────────────
function ManualCompleteDialog({ task, onClose, onDone }) {
  const [profileId, setProfileId] = useState('')
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!profileId.trim()) { setError('Profile ID is required'); return }
    setLoading(true)
    setError(null)
    try {
      await adminManualCompleteTask(task.id, profileId.trim(), note.trim() || undefined)
      onDone()
    } catch (err) {
      setError(err.message ?? 'Failed to credit user')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title="Manually Complete" onClose={onClose}>
      <p className="text-[12px] text-on-surface-variant">
        Credits <span className="font-bold text-on-surface">{task.rewardAmount} CPT</span> directly for
        <span className="font-bold text-on-surface"> {task.title}</span>, no proof required.
      </p>
      {error && <AdminAlert type="error" message={error} />}
      <Field label="User profile ID">
        <input className={inputCls} value={profileId} onChange={e => setProfileId(e.target.value)} />
      </Field>
      <Field label="Note (optional)">
        <input className={inputCls} placeholder="e.g. Handled via support ticket #482" value={note} onChange={e => setNote(e.target.value)} />
      </Field>
      <div className="flex gap-2 mt-1">
        <button onClick={onClose} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={submit} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-50">
          {loading ? 'Crediting…' : 'Credit User'}
        </button>
      </div>
    </Dialog>
  )
}

// ── Reject reason dialog ─────────────────────────────────────────────────
function RejectDialog({ submission, onClose, onDone }) {
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!reason.trim()) { setError('A reason is required'); return }
    setLoading(true)
    setError(null)
    try {
      await adminRejectTaskSubmission(submission.id, reason.trim())
      onDone()
    } catch (err) {
      setError(err.message ?? 'Failed to reject submission')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title="Reject Submission" onClose={onClose}>
      {error && <AdminAlert type="error" message={error} />}
      <Field label="Reason">
        <textarea rows={3} className={`${inputCls} resize-y`} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. Link doesn't contain the required keyword" />
      </Field>
      <div className="flex gap-2 mt-1">
        <button onClick={onClose} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={submit} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-error bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-colors disabled:opacity-50">
          {loading ? 'Rejecting…' : 'Reject'}
        </button>
      </div>
    </Dialog>
  )
}

// ── Task list ─────────────────────────────────────────────────────────────
function TaskRow({ task, onEdit, onDeactivate, onDelete, onStats, onManualComplete }) {
  return (
    <div className="border-4 border-on-background bg-surface p-4 flex items-center justify-between gap-3 flex-wrap"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest">{task.type}</span>
          <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${
            task.status === 'active' ? 'border-primary-container text-on-primary-container bg-primary-container/15'
            : task.status === 'paused' ? 'border-on-background/30 text-on-surface-variant'
            : 'border-error text-error'
          }`}>{task.status}</span>
          {task.recurring && (
            <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest">
              Recurring · {task.cooldownHours}h
            </span>
          )}
        </div>
        <p className="font-bold text-[14px]">{task.title}</p>
        <p className="text-[11px] text-on-surface-variant truncate max-w-lg">{task.description}</p>
        <p className="text-[11px] text-on-surface-variant font-mono mt-1">
          {task.rewardAmount} CPT · {fmt(task.completionsCount)}{task.completionCap ? ` / ${task.completionCap}` : ''} completions
        </p>
      </div>
      <div className="flex gap-2 shrink-0 flex-wrap">
        <button onClick={() => onStats(task)}
          className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
          Stats
        </button>
        <button onClick={() => onManualComplete(task)}
          className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
          Manual Credit
        </button>
        <button onClick={() => onEdit(task)}
          className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
          Edit
        </button>
        {task.status !== 'ended' && (
          <button onClick={() => onDeactivate(task)}
            className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
            Deactivate
          </button>
        )}
        <button onClick={() => onDelete(task)}
          className="px-3 py-2 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors">
          Delete
        </button>
      </div>
    </div>
  )
}

// ── Review queue ──────────────────────────────────────────────────────────
function SubmissionRow({ submission, onApprove, onReject, actingOn }) {
  const busy = actingOn === submission.id
  return (
    <div className="border-4 border-on-background bg-surface p-4 flex items-center justify-between gap-3 flex-wrap"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${
            submission.status === 'approved' ? 'border-primary-container text-on-primary-container bg-primary-container/15'
            : submission.status === 'rejected' ? 'border-error text-error'
            : 'border-on-background/30 text-on-surface-variant'
          }`}>{submission.status}</span>
          {submission.manuallyCredited && (
            <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest">Manual</span>
          )}
        </div>
        <p className="font-bold text-[13px]">{submission.task?.title ?? 'Unknown task'}</p>
        <p className="text-[11px] text-on-surface-variant font-mono truncate">User: {submission.profileId}</p>
        {submission.proofUrl && (
          <a href={submission.proofUrl} target="_blank" rel="noopener noreferrer"
            className="text-[11px] font-bold text-primary-container hover:underline truncate block">
            {submission.proofUrl}
          </a>
        )}
        {submission.rejectionReason && (
          <p className="text-[11px] text-error mt-1">Reason: {submission.rejectionReason}</p>
        )}
        <p className="text-[11px] text-on-surface-variant font-mono mt-1">
          {submission.createdAt ? new Date(submission.createdAt).toLocaleString() : ''}
        </p>
      </div>
      {submission.status === 'pending' && (
        <div className="flex gap-2 shrink-0">
          <button onClick={() => onApprove(submission)} disabled={busy}
            className="px-3 py-2 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[10px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-40">
            Approve
          </button>
          <button onClick={() => onReject(submission)} disabled={busy}
            className="px-3 py-2 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors disabled:opacity-40">
            Reject
          </button>
        </div>
      )}
    </div>
  )
}

export function TaskManagement() {
  const [view, setView] = useState('tasks') // 'tasks' | 'queue'

  const [taskStatus, setTaskStatus] = useState(undefined)
  const [tasks, setTasks] = useState([])
  const [tasksLoading, setTasksLoading] = useState(true)
  const [tasksError, setTasksError] = useState(null)

  const [subStatus, setSubStatus] = useState('pending')
  const [submissions, setSubmissions] = useState([])
  const [subsLoading, setSubsLoading] = useState(true)
  const [subsError, setSubsError] = useState(null)

  const [formTask, setFormTask] = useState(null) // null closed, {} for new, task for edit
  const [statsTask, setStatsTask] = useState(null)
  const [manualTask, setManualTask] = useState(null)
  const [deactivateTarget, setDeactivateTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [rejectTarget, setRejectTarget] = useState(null)
  const [actingOn, setActingOn] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const loadTasks = useCallback(() => {
    setTasksLoading(true)
    setTasksError(null)
    adminGetTasks(taskStatus).then(setTasks).catch(setTasksError).finally(() => setTasksLoading(false))
  }, [taskStatus])

  const loadSubmissions = useCallback(() => {
    setSubsLoading(true)
    setSubsError(null)
    adminGetTaskSubmissions({ status: subStatus }).then(setSubmissions).catch(setSubsError).finally(() => setSubsLoading(false))
  }, [subStatus])

  useEffect(() => { loadTasks() }, [loadTasks])
  useEffect(() => { loadSubmissions() }, [loadSubmissions])

  if (tasksError && isNotBuiltYet(tasksError) && tasks.length === 0 && !tasksLoading) {
    return <AdminComingSoon label="Social Tasks" />
  }

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(null), 3000) }

  const doDeactivate = async () => {
    setActingOn(deactivateTarget.id)
    try {
      await adminDeactivateTask(deactivateTarget.id)
      flash('Task deactivated.')
      setDeactivateTarget(null)
      loadTasks()
    } catch (err) {
      setActionError(err.message ?? 'Failed to deactivate task')
    } finally {
      setActingOn(null)
    }
  }

  const doDelete = async () => {
    setActingOn(deleteTarget.id)
    try {
      await adminDeleteTask(deleteTarget.id)
      flash('Task deleted.')
      setDeleteTarget(null)
      loadTasks()
    } catch (err) {
      setActionError(err.message ?? 'Failed to delete task')
      setDeleteTarget(null)
    } finally {
      setActingOn(null)
    }
  }

  const doApprove = async (submission) => {
    setActingOn(submission.id)
    setActionError(null)
    try {
      await adminApproveTaskSubmission(submission.id)
      flash('Submission approved and reward credited.')
      loadSubmissions()
      loadTasks()
    } catch (err) {
      setActionError(err.message ?? 'Failed to approve submission')
    } finally {
      setActingOn(null)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {formTask !== null && (
        <TaskFormDialog
          task={formTask.id ? formTask : null}
          onClose={() => setFormTask(null)}
          onSaved={() => { setFormTask(null); flash(formTask.id ? 'Task updated.' : 'Task created.'); loadTasks() }}
        />
      )}
      {statsTask && <StatsDialog taskId={statsTask.id} onClose={() => setStatsTask(null)} />}
      {manualTask && (
        <ManualCompleteDialog
          task={manualTask}
          onClose={() => setManualTask(null)}
          onDone={() => { setManualTask(null); flash('User credited.'); loadTasks(); loadSubmissions() }}
        />
      )}
      {rejectTarget && (
        <RejectDialog
          submission={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onDone={() => { setRejectTarget(null); flash('Submission rejected.'); loadSubmissions() }}
        />
      )}

      {deactivateTarget && (
        <Dialog title="Deactivate Task" onClose={() => setDeactivateTarget(null)}>
          <p className="text-[12px] text-on-surface-variant">
            This hides <span className="font-bold text-on-surface">{deactivateTarget.title}</span> from users. Existing submission history is kept.
          </p>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setDeactivateTarget(null)} disabled={actingOn === deactivateTarget.id}
              className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={doDeactivate} disabled={actingOn === deactivateTarget.id}
              className="flex-1 px-4 py-2.5 border-2 border-on-background bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-colors disabled:opacity-50">
              {actingOn === deactivateTarget.id ? 'Working…' : 'Deactivate'}
            </button>
          </div>
        </Dialog>
      )}

      {deleteTarget && (
        <Dialog title="Delete Task" onClose={() => setDeleteTarget(null)}>
          <p className="text-[12px] text-on-surface-variant">
            Permanently deletes <span className="font-bold text-on-surface">{deleteTarget.title}</span>. Only allowed if it has zero submissions — otherwise deactivate it instead.
          </p>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setDeleteTarget(null)} disabled={actingOn === deleteTarget.id}
              className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={doDelete} disabled={actingOn === deleteTarget.id}
              className="flex-1 px-4 py-2.5 border-2 border-error bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-colors disabled:opacity-50">
              {actingOn === deleteTarget.id ? 'Working…' : 'Delete'}
            </button>
          </div>
        </Dialog>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 border-2 border-on-background w-fit">
          <button onClick={() => setView('tasks')}
            className={`px-4 py-2 font-bold text-[11px] uppercase tracking-widest transition-colors ${view === 'tasks' ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'}`}>
            Tasks
          </button>
          <button onClick={() => setView('queue')}
            className={`px-4 py-2 font-bold text-[11px] uppercase tracking-widest transition-colors ${view === 'queue' ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'}`}>
            Review Queue
          </button>
        </div>
        {view === 'tasks' && (
          <button onClick={() => setFormTask({})}
            className="px-4 py-2 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors">
            + New Task
          </button>
        )}
      </div>

      {successMsg && <AdminAlert type="success" message={successMsg} />}
      {actionError && <AdminAlert type="error" message={actionError} />}

      {view === 'tasks' ? (
        <div className="flex flex-col gap-4">
          <div className="flex gap-1 border-2 border-on-background w-fit">
            {STATUS_FILTERS.map(f => (
              <button key={f.label} onClick={() => setTaskStatus(f.key)}
                className={`px-3 py-1.5 font-bold text-[11px] uppercase tracking-widest transition-colors ${
                  taskStatus === f.key ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'
                }`}>
                {f.label}
              </button>
            ))}
          </div>

          {tasksError && !isNotBuiltYet(tasksError) && (
            <AdminAlert type="error" message={tasksError.message ?? 'Failed to load tasks'} />
          )}

          {tasksLoading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
          ) : tasks.length === 0 ? (
            <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
              <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No tasks match these filters</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {tasks.map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onEdit={setFormTask}
                  onDeactivate={setDeactivateTarget}
                  onDelete={setDeleteTarget}
                  onStats={setStatsTask}
                  onManualComplete={setManualTask}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex gap-1 border-2 border-on-background w-fit">
            {SUBMISSION_FILTERS.map(f => (
              <button key={f.label} onClick={() => setSubStatus(f.key)}
                className={`px-3 py-1.5 font-bold text-[11px] uppercase tracking-widest transition-colors ${
                  subStatus === f.key ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'
                }`}>
                {f.label}
              </button>
            ))}
          </div>

          {subsError && !isNotBuiltYet(subsError) && (
            <AdminAlert type="error" message={subsError.message ?? 'Failed to load submissions'} />
          )}

          {subsLoading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
          ) : submissions.length === 0 ? (
            <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
              <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No submissions match these filters</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {submissions.map(s => (
                <SubmissionRow key={s.id} submission={s} onApprove={doApprove} onReject={setRejectTarget} actingOn={actingOn} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
